/**
 * App Preferences Manager
 *
 * Manages application-level preferences that persist across user sessions.
 * These preferences are tied to the browser/device, NOT to a specific user.
 *
 * Examples:
 * - Has user seen the preferences modal?
 * - UI preferences (theme, language, etc.)
 * - Onboarding completion flags
 * - Feature tour states
 *
 * IMPORTANT: These preferences should NOT be cleared on logout,
 * as they represent the application state on this device/browser.
 */

const APP_PREFERENCES_MODAL_SHOWN_KEY = 'meiuca-app-preferences-modal-shown'
const ANALYTICS_CONSENT_KEY = 'meiuca-analytics-consent'

class AppPreferencesManager {
  /**
   * Check if the preferences modal has been shown to the user
   * (on this device/browser, regardless of which user)
   */
  hasShownPreferencesModal(): boolean {
    try {
      return localStorage.getItem(APP_PREFERENCES_MODAL_SHOWN_KEY) === 'true'
    } catch {
      return false
    }
  }

  /**
   * Mark preferences modal as shown on this device/browser
   */
  markPreferencesModalAsShown(): void {
    try {
      localStorage.setItem(APP_PREFERENCES_MODAL_SHOWN_KEY, 'true')
      console.log('[AppPreferencesManager] Preferences modal marked as shown')
    } catch (error) {
      console.warn('[AppPreferencesManager] Error marking modal as shown:', error)
    }
  }

  /**
   * Reset preferences modal state
   * Useful for testing or if user wants to see the modal again
   */
  resetPreferencesModalState(): void {
    try {
      localStorage.removeItem(APP_PREFERENCES_MODAL_SHOWN_KEY)
      console.log('[AppPreferencesManager] Preferences modal state reset')
    } catch (error) {
      console.error('[AppPreferencesManager] Error resetting modal state:', error)
    }
  }

  /**
   * Analytics consent (LGPD opt-out). Device-level; NOT cleared on logout.
   * Default: granted (legitimate interest) until the user explicitly opts out.
   */
  getAnalyticsConsent(): boolean {
    try {
      return localStorage.getItem(ANALYTICS_CONSENT_KEY) !== 'false'
    } catch {
      return true
    }
  }

  setAnalyticsConsent(consent: boolean): void {
    try {
      localStorage.setItem(ANALYTICS_CONSENT_KEY, consent ? 'true' : 'false')
    } catch (error) {
      console.warn('[AppPreferencesManager] Error setting analytics consent:', error)
    }
  }

  /**
   * Clear all app preferences
   * NOTE: This should NOT be called on logout! Only for testing or full app reset.
   */
  clearAll(): void {
    this.resetPreferencesModalState()
    // Add other app preferences clearing here as needed
    console.log('[AppPreferencesManager] All app preferences cleared')
  }
}

// Export singleton instance
export const appPreferencesManager = new AppPreferencesManager()
